import { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router";
import { useAppDispatch, useAppSelector } from "@/stores/hooks";
import { setLogout } from "@/api/redux-toolkit/reducer/authSlice";
import { cognitoSignOut } from "@/services/cognito-auth";
import {
  OIDC_ENABLED,
  broadcastLogout,
  endSession,
  logoutRequest,
  stopSessionRefresh,
} from "@penn/web-common";
import { TOKEN_STORAGE_KEY, USER_STORAGE_KEY } from "@/config/auth-storage-keys";
import { authStorage } from "@/config/session-storage";
import {
  Menu, Search, Sun, Moon, ChevronDown,
  LogOut, User, X, ExternalLink,
} from "lucide-react";
import { useSidebar } from "@/contexts/SidebarContext";
import { useTheme } from "@/contexts/ThemeContext";
import { SignOutConfirm } from "@/components/popup/auth/SignOutConfirm";

/* ── Breadcrumb ── */
const routeLabels: Record<string, string> = {
  users: "Users",
  roles: "Roles",
  applications: "Applications",
  tasks: "Tasks",
  smtp: "SMTP",
  analytics: "Analytics",
  "change-log": "Changelog",
  datasets: "Datasets",
  "machine-options": "Machine Options EU",
  "machine-options-us": "Machine Options US",
  products: "Products",
  "landing-content": "Landing Content",
  "cad-3d-settings": "CAD 3D Settings",
  "cost-models": "Cost Models",
  "calculation-parameters": "Calculation Parameters",
  "responsibility-map": "Responsibility Map",
  "manual-quote-items": "Manual Quote Items",
  fasteners: "Fasteners",
  "profil-fasteners": "Profil Fasteners",
  "autoriv-fasteners": "Autoriv Fasteners",
  "autoriv-configurator-items": "Autoriv Configurator Items EU",
  "autoriv-configurator-items-us": "Autoriv Configurator Items US",
  "sherex-fasteners": "Sherex Fasteners",
  "sherex-options": "Sherex Options EU",
  "sherex-options-us": "Sherex Options US",
  "profil-indie-options": "Profil InDie Options EU",
  "profil-indie-options-us": "Profil InDie Options US",
  "fastener-tooling-mapping": "Tooling Mapping",
  "tooling-sets": "Tooling Sets",
  "pemserter-eur": "Pemserter EUR",
  "pemserter-usd": "Pemserter USD",
  "tasks-raw": "Raw JSON Tasks",
  dashboard: "Dashboard",
  overview: "Overview",
  reports: "Reports",
  settings: "Settings",
  general: "General",
  permissions: "Permissions",
  profile: "Profile",
  security: "Security",
};

const Breadcrumb = () => {
  const location = useLocation();
  // Admin routes are mounted under "/admin"; drop that leading segment so the
  // breadcrumb doesn't repeat the static "Admin" crumb rendered below.
  const parts = location.pathname
    .split("/")
    .filter(Boolean)
    .filter((p, i) => !(i === 0 && p === "admin"));

  return (
    <nav className="flex items-center gap-1 text-nav text-on-navy-tertiary" aria-label="Breadcrumb">
      <span className="text-on-navy-secondary font-medium">Admin</span>
      {parts.map((part, i) => (
        <span key={i} className="flex items-center gap-1">
          <span className="text-on-navy-tertiary">/</span>
          <span className={i === parts.length - 1 ? "text-on-navy font-medium" : ""}>
            {routeLabels[part] ?? part}
          </span>
        </span>
      ))}
    </nav>
  );
};

/* ── Notification dot ── */
const NotificationButton = () => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div ref={ref} className="relative">
      {/* <button
        onClick={() => setOpen((v) => !v)}
        className="relative flex items-center justify-center w-8 h-8 rounded-lg text-on-navy-secondary hover:bg-white/15 dark:hover:bg-gray-800 hover:text-white dark:hover:text-gray-200 transition-colors"
        aria-label="Notifications"
      >
        <Bell size={16} />
        <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full bg-penn-500" />
      </button> */}
      {open && (
        <div className="absolute right-0 top-10 w-72 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl shadow-lg shadow-gray-200/50 dark:shadow-gray-950/50 overflow-hidden z-50">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-gray-800">
            <span className="text-h4 text-blue_grey">Notifications</span>
            <span className="text-small-medium text-penn-500 cursor-pointer hover:text-penn-500">Mark all read</span>
          </div>
          <div className="divide-y divide-gray-100 dark:divide-gray-800">
            {[
              { title: "New user registered", time: "2 min ago", dot: "bg-penn-500" },
              { title: "Dataset updated: Fasteners", time: "1 hour ago", dot: "bg-green-500" },
              { title: "Task completed", time: "3 hours ago", dot: "bg-purple-500" },
            ].map((n, i) => (
              <div key={i} className="flex items-start gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors cursor-pointer">
                <span className={`mt-2 w-2 h-2 rounded-full flex-shrink-0 ${n.dot}`} />
                <div>
                  <p className="text-body-medium text-blue_grey">{n.title}</p>
                  <p className="text-small text-blue_grey4 mt-1">{n.time}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="px-4 py-3 border-t border-gray-100 dark:border-gray-800 text-center">
            <span className="text-small-medium text-penn-500 cursor-pointer hover:text-penn-500">View all notifications</span>
          </div>
        </div>
      )}
    </div>
  );
};

/* ── Theme toggle ── */
// Parked component: exported (not yet mounted in the header) so the strict
// production `tsc -b` build does not flag it under noUnusedLocals.
export const ThemeToggle = () => {
  const { theme, toggleTheme } = useTheme();
  return (
    <button
      onClick={toggleTheme}
      className="flex items-center justify-center w-8 h-8 rounded-lg text-on-navy-secondary hover:bg-white/15 dark:hover:bg-gray-800 hover:text-white dark:hover:text-gray-200 transition-colors"
      aria-label="Toggle theme"
    >
      {theme === "light" ? <Moon size={16} /> : <Sun size={16} />}
    </button>
  );
};

/* ── Profile dropdown ── */
const ProfileDropdown = () => {
  const [open, setOpen] = useState(false);
  const [showSignOutConfirm, setShowSignOutConfirm] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const user = useAppSelector((s) => s.auth.user);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleLogout = async () => {
    // Same sequence as the customer NavBar, and every step earns its place.
    stopSessionRefresh();
    // Server logout FIRST: it deletes the auth_sessions row and clears both
    // cookies. Skipping it leaves penn_rt alive, so the next boot refresh signs
    // the user straight back in — and the session stays valid server-side.
    try {
      await logoutRequest();
    } catch {
      /* best effort — a failed call must not strand a half-logged-out UI */
    }
    // Must not throw: Amplify rejects when it has no session of its own (the
    // normal case under OIDC), which would abort the teardown below.
    try {
      await cognitoSignOut();
    } catch {
      /* ignore */
    }
    dispatch(setLogout());
    authStorage.removeItem(TOKEN_STORAGE_KEY);
    authStorage.removeItem(USER_STORAGE_KEY);
    // Other tabs hold their own per-tab session and would stay signed in.
    broadcastLogout();
    if (OIDC_ENABLED) {
      // End the ISSUER session too, or its SSO cookie silently re-admits the
      // same account on the next /login. Full-page navigation: must be last.
      endSession();
      return;
    }
    navigate("/login");
  };

  const initials = user
    ? `${user.firstName?.[0] ?? ""}${user.lastName?.[0] ?? ""}`.toUpperCase()
    : "A";
  const fullName = user ? `${user.firstName} ${user.lastName}` : "Admin";

  return (
    <>
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 px-2 py-2 rounded-lg hover:bg-white/15 dark:hover:bg-gray-800 transition-colors"
        aria-expanded={open}
        aria-haspopup="menu"
      >
        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-penn-500 to-penn-700 flex items-center justify-center text-small-medium text-white">
          {initials}
        </div>
        <span className="hidden sm:flex items-center gap-2 min-w-0">
          <span className="text-body-medium text-on-navy max-w-25 truncate">
            {fullName}
          </span>
          {user?.role?.displayName && (
            <span className="shrink-0 rounded-full bg-penn-100 dark:bg-penn-900 px-2 py-0.5 text-caption text-penn-700 dark:text-penn-100">
              {user.role.displayName}
            </span>
          )}
        </span>
        <ChevronDown size={13} className="text-on-navy-tertiary" />
      </button>

      {open && (
        <div className="absolute right-0 top-10 w-60 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl shadow-lg shadow-gray-200/50 dark:shadow-gray-950/50 overflow-hidden z-50">
          <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-800">
            <p className="text-body-semibold text-blue_grey">{fullName}</p>
            <p className="text-blue_grey4 truncate mt-1 text-small">{user?.email ?? ""}</p>
          </div>
          <div className="py-1">
            {[
              { icon: <User size={14} />, label: "Profile", onClick: () => navigate("/admin/settings/profile") },
              { icon: <ExternalLink size={14} />, label: "Back to Selector", onClick: () => navigate("/home") },
              // { icon: <Settings size={14} />, label: "Settings", onClick: () => navigate("/admin/settings/general") },
            ].map((item) => (
              <button
                key={item.label}
                onClick={() => { item.onClick(); setOpen(false); }}
                className="w-full flex items-center gap-3 px-4 py-3 text-body text-blue_grey2 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-blue_grey dark:hover:text-gray-100 transition-colors"
              >
                <span className="text-blue_grey4">{item.icon}</span>
                {item.label}
              </button>
            ))}
          </div>
          <div className="py-1 border-t border-gray-100 dark:border-gray-800">
            <button
              onClick={() => { setOpen(false); setShowSignOutConfirm(true); }}
              className="w-full flex items-center gap-3 px-4 py-3 text-body text-red-500 hover:bg-red-50 dark:hover:bg-red-950/40 transition-colors"
            >
              <LogOut size={14} />
              Sign out
            </button>
          </div>
        </div>
      )}
    </div>

    {showSignOutConfirm && (
      <SignOutConfirm
        onConfirm={handleLogout}
        onCancel={() => setShowSignOutConfirm(false)}
      />
    )}
    </>
  );
};

/* ── Search bar ── */
// Parked component: exported (not yet mounted in the header) so the strict
// production `tsc -b` build does not flag it under noUnusedLocals.
export const SearchBar = () => {
  const [focused, setFocused] = useState(false);
  const [query, setQuery] = useState("");

  return (
    <div className={[
      "hidden md:flex items-center gap-2 px-3 h-8 rounded-lg border text-body transition-all duration-200 bg-white/15 dark:bg-gray-800",
      focused
        ? "border-penn-400 dark:border-penn-500 w-56 shadow-sm shadow-penn-900/30"
        : "border-white/30 dark:border-gray-700 w-44",
    ].join(" ")}>
      <Search size={13} className="text-on-navy-tertiary flex-shrink-0" />
      <input
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        placeholder="Search…"
        className="flex-1 bg-transparent outline-none text-on-navy placeholder-white/60 dark:placeholder-gray-500"
      />
      {query && (
        <button onClick={() => setQuery("")} className="text-on-navy-tertiary hover:text-white dark:hover:text-gray-300 transition-colors">
          <X size={12} />
        </button>
      )}
    </div>
  );
};

/* ── Header ── */
export const AdminHeader = () => {
  const { toggleMobile, toggleCollapsed } = useSidebar();

  return (
    <header className="h-14 bg-linear-to-b from-penn-900 via-penn-700 to-[#005A9E] border-b border-white/15 dark:bg-gray-900 dark:border-gray-800 flex items-center justify-between px-4 gap-3 shrink-0 z-30">
      {/* Left */}
      <div className="flex items-center gap-3">
        <button
          onClick={toggleMobile}
          className="lg:hidden flex items-center justify-center w-8 h-8 rounded-lg text-on-navy-secondary hover:bg-white/15 dark:hover:bg-gray-800 transition-colors"
          aria-label="Toggle menu"
        >
          <Menu size={18} />
        </button>
        <button
          onClick={toggleCollapsed}
          className="hidden lg:flex items-center justify-center w-8 h-8 rounded-lg text-on-navy-secondary hover:bg-white/15 dark:hover:bg-gray-800 transition-colors"
          aria-label="Toggle sidebar"
        >
          <Menu size={18} />
        </button>
        <div className="hidden sm:block">
          <Breadcrumb />
        </div>
      </div>

      {/* Right */}
      <div className="flex items-center gap-2">
        {/* <SearchBar /> */}
        {/* <ThemeToggle /> */}
        <NotificationButton />
        {/* <div className="w-px h-5 bg-gray-200 dark:bg-gray-700 mx-1" /> */}
        <ProfileDropdown />
      </div>
    </header>
  );
};
